import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  ConversationInfo,
  MessageInfo,
  UserInfo,
} from '@/api/types';
import { UserService } from '@/api/services/userService';
import { logPrint } from '@/utils/my-utils';
import { TranslationSettings, DEFAULT_TRANSLATION_SETTINGS } from '@/hooks/useTranslation';

interface ChatState {
  // 链接状态
  isConnected: boolean;

  // 对话列表数据
  conversations: ConversationInfo[];

  // 当前对话未确认消息
  unackedMessages: Record<string, MessageInfo[]>;

  // 历史消息
  messages: Record<string, MessageInfo[]>;

  // 用户信息缓存
  userInfoCache: Map<string, UserInfo>;

  // 当前活跃对话
  activeConversationId: string;

  // 翻译设置存储 - 每个会话可以有不同的翻译设置
  translationSettings: Record<string, TranslationSettings>;

  // 全局默认翻译设置
  defaultTranslationSettings: TranslationSettings;

  // 数据设置方法（仅负责状态管理）
  setIsConnected: (isConnected: boolean) => void;
  setConversations: (conversations: ConversationInfo[]) => void;

  setUnackedMessages: (conversationId: string, messages: MessageInfo[]) => void;

  setMessages: (conversationId: string, messages: MessageInfo[]) => void;
  prependMessages: (conversationId: string, messages: MessageInfo[]) => void;


  setActiveConversation: (conversationId: string) => void;

  // 本地状态更新方法
  addConversation: (conversation: ConversationInfo) => void;
  updateConversation: (conversationId: string, updates: Partial<ConversationInfo>) => void;
  removeConversation: (conversationId: string) => void;
  addMessage: (conversationId: string, message: MessageInfo) => void;
  updateMessage: (conversationId: string, messageId: string, updates: Partial<MessageInfo>) => void;
  removeMessage: (conversationId: string, messageId: string) => void;
  markMessagesAsRead: (conversationId: string, messageIds: string[]) => void;
  
  // 未读消息计数管理
  incrementUnreadCount: (conversationId: string) => void;
  clearUnreadCount: (conversationId: string) => void;
  getUnreadCount: (conversationId: string) => number;
  
  // 会话排序管理
  moveConversationToTop: (conversationId: string) => void;
  
  // 用户信息缓存管理
  getUserByCacheOrNet: (userId: string) => Promise<UserInfo | null>;
  setUserToCache: (userId: string, userInfo: UserInfo) => void;
  clearUserCache: () => void;

  // 翻译设置管理
  getTranslationSettings: (conversationId: string) => TranslationSettings;
  setTranslationSettings: (conversationId: string, settings: TranslationSettings) => void;
  updateTranslationSettings: (conversationId: string, updates: Partial<TranslationSettings>) => void;
  removeTranslationSettings: (conversationId: string) => void;
  setDefaultTranslationSettings: (settings: TranslationSettings) => void;
  resetTranslationSettings: (conversationId: string) => void;
}

/**
 * 聊天数据管理 Store
 * 仅负责数据保存和状态管理，不包含网络请求逻辑
 */
export const useChatStore: any = create<ChatState>()(
  subscribeWithSelector((set) => ({
    isConnected: false,

    // 初始状态
    conversations: [] as ConversationInfo[],

    unackedMessages: {},

    messages: {},
    messagesLastEvaluatedKey: {},

    userInfoCache: new Map<string, UserInfo>(),

    activeConversationId: '',

    // 翻译设置初始化
    translationSettings: {},
    defaultTranslationSettings: DEFAULT_TRANSLATION_SETTINGS,

    setIsConnected: (isConnected: boolean) => {
      set({ isConnected });
    },

    // 对话列表状态设置
    setConversations: (conversations: ConversationInfo[]) => {
      set({ conversations });
    },


    // 未确认消息状态设置
    setUnackedMessages: (conversationId: string, messages: MessageInfo[]) => {
      set(state => ({
        unackedMessages: {
          ...state.unackedMessages,
          [conversationId]: messages
        }
      }));
    },


    // 历史消息状态设置
    setMessages: (conversationId: string, messages: MessageInfo[]) => {
      // 按时间正序排序（最新消息在最后）
      const sortedMessages = [...messages].sort((a, b) => {
        const timeA = (a.receiver_time ?? a.message_id_time) || 0;
        const timeB = (b.receiver_time ?? b.message_id_time) || 0;
        return timeA - timeB;
      });

      set(state => ({
        messages: {
          ...state.messages,
          [conversationId]: sortedMessages
        }
      }));
    },

    prependMessages: (conversationId: string, messages: MessageInfo[]) => {
      set(state => {
        const currentMessages = state.messages[conversationId] || [];
        
        // 过滤掉已存在的消息，避免重复
        const newMessages = messages.filter(newMsg => 
          !currentMessages.some(existingMsg => existingMsg.message_id === newMsg.message_id)
        );
        
        // 合并消息并按时间正序排序（最新消息在最后）
        const allMessages = [...newMessages, ...currentMessages].sort((a, b) => {
          const timeA = (a.receiver_time ?? a.message_id_time) || 0;
          const timeB = (b.receiver_time ?? b.message_id_time) || 0;
          return timeA - timeB;
        });

        return {
          messages: {
            ...state.messages,
            [conversationId]: allMessages
          }
        };
      });
    },

    // 当前活跃对话设置
    setActiveConversation: (conversationId: string) => {
      set({ activeConversationId: conversationId });
    },

    // 本地状态更新方法
    /**
     * 添加对话到列表（本地更新）
     * @param conversation 对话信息
     */
    addConversation: (conversation: ConversationInfo) => {
      set(state => ({
        conversations: [conversation, ...state.conversations]
      }));
    },

    /**
     * 更新对话信息（本地更新）
     * @param conversationId 对话ID
     * @param updates 更新的字段
     */
    updateConversation: (conversationId: string, updates: Partial<ConversationInfo>) => {
      set(state => ({
        conversations: state.conversations.map(conversation =>
          conversation.id === conversationId
            ? { ...conversation, ...updates }
            : conversation
        )
      }));
    },

    /**
     * 移除对话（本地更新）
     * @param conversationId 对话ID
     */
    removeConversation: (conversationId: string) => {
      set(state => {
        const { [conversationId]: _, ...restUnackedMessages } = state.unackedMessages;
        const { [conversationId]: __, ...restMessages } = state.messages;

        return {
          conversations: state.conversations.filter(
            conversation => conversation.id !== conversationId
          ),
          unackedMessages: restUnackedMessages,
          messages: restMessages,
        };
      });
    },

    /**
     * 添加消息到对话（本地更新）
     * @param conversationId 对话ID
     * @param message 消息信息
     */
    addMessage: (conversationId: string, message: MessageInfo) => {
      set(state => {
        const currentUnackedMessages = state.unackedMessages[conversationId] || [];
        const currentMessages = state.messages[conversationId] || [];

        // 检查消息是否已存在，避免重复添加
        const messageExists = currentMessages.some(msg => msg.message_id === message.message_id);
        if (messageExists) {
          logPrint(`消息 ${message.message_id} 已存在，跳过添加`);
          return state; // 不更新状态
        }

        // 添加新消息并按时间排序
        const updatedMessages = [...currentMessages, message].sort((a, b) => {
          const timeA = (a.receiver_time ?? a.message_id_time) || new Date(a.receiver_time ?? a.message_id_time).getTime() || 0;
          const timeB = (b.receiver_time ?? b.message_id_time) || new Date(b.receiver_time ?? b.message_id_time).getTime() || 0;
          return timeA - timeB;
        });

        // 对于未确认消息，也需要去重
        const unackedExists = currentUnackedMessages.some(msg => msg.message_id === message.message_id);
        const updatedUnackedMessages = unackedExists
          ? currentUnackedMessages
          : [...currentUnackedMessages, message];

        // 当有新消息添加时，更新对话的最后消息时间并重新排序
        const conversationIndex = state.conversations.findIndex(conv => conv.id === conversationId);
        let updatedConversations = [...state.conversations];
        
        if (conversationIndex >= 0) {
          // 更新对话的 last_read_at 为新消息的时间
          const messageTime = message.receiver_time || Number(message.message_id_time) || null;
          updatedConversations[conversationIndex] = {
            ...updatedConversations[conversationIndex],
            last_read_at: messageTime || null
          };
          
          // 使用正确的排序逻辑重新排序对话列表
          // 分离置顶和非置顶对话
          const pinnedConversations = updatedConversations.filter(conv => conv.is_pinned);
          const unpinnedConversations = updatedConversations.filter(conv => !conv.is_pinned);

          // 置顶对话内部排序：按最后消息时间排序
          const sortedPinned = pinnedConversations.sort((a, b) => {
            const timeA = (a.last_read_at || 0);
            const timeB = (b.last_read_at || 0);
            return timeB - timeA;
          });

          // 非置顶对话排序：按最后消息时间排序
          const sortedUnpinned = unpinnedConversations.sort((a, b) => {
            const timeA = (a.last_read_at || 0);
            const timeB = (b.last_read_at || 0);
            return timeB - timeA;
          });

          // 置顶对话永远在前面，后面跟非置顶对话
          updatedConversations = [...sortedPinned, ...sortedUnpinned];
        }

        return {
          conversations: updatedConversations,
          unackedMessages: {
            ...state.unackedMessages,
            [conversationId]: updatedUnackedMessages
          },
          messages: {
            ...state.messages,
            [conversationId]: updatedMessages
          }
        };
      });
    },

    /**
     * 更新消息信息（本地更新）
     * @param conversationId 对话ID
     * @param messageId 消息ID
     * @param updates 更新的字段
     */
    updateMessage: (conversationId: string, messageId: string, updates: Partial<MessageInfo>) => {
      set(state => {
        const currentUnackedMessages = state.unackedMessages[conversationId] || [];
        const currentMessages = state.messages[conversationId] || [];
        return {
          unackedMessages: {
            ...state.unackedMessages,
            [conversationId]: currentUnackedMessages.map(message =>
              message.message_id === messageId
                ? { ...message, ...updates }
                : message
            )
          },
          messages: {
            ...state.messages,
            [conversationId]: currentMessages.map(message =>
              message.message_id === messageId
                ? { ...message, ...updates }
                : message
            )
          }
        };
      });
    },

    /**
     * 移除消息（本地更新）
     * @param conversationId 对话ID
     * @param messageId 消息ID
     */
    removeMessage: (conversationId: string, messageId: string) => {
      set(state => {
        const currentUnackedMessages = state.unackedMessages[conversationId] || [];
        const currentMessages = state.messages[conversationId] || [];
        return {
          unackedMessages: {
            ...state.unackedMessages,
            [conversationId]: currentUnackedMessages.filter(message => message.message_id !== messageId)
          },
          messages: {
            ...state.messages,
            [conversationId]: currentMessages.filter(message => message.message_id !== messageId)
          }
        };
      });
    },

    /**
     * 标记消息为已读（本地更新）
     * @param conversationId 对话ID
     * @param messageIds 消息ID列表
     */
    markMessagesAsRead: (conversationId: string, messageIds: string[]) => {
      set(state => {
        const currentMessages = state.unackedMessages[conversationId] || [];
        return {
          unackedMessages: {
            ...state.unackedMessages,
            [conversationId]: currentMessages.filter(message =>
              !messageIds.includes(message.message_id)
            )
          }
        };
      });
    },

    /**
     * 增加指定对话的未读消息计数
     * @param conversationId 对话ID
     */
    incrementUnreadCount: (conversationId: string) => {
      set(state => ({
        conversations: state.conversations.map(conversation =>
          conversation.id === conversationId
            ? { ...conversation, unreadCount: (conversation.unreadCount || 0) + 1 }
            : conversation
        )
      }));
    },

    /**
     * 清零指定对话的未读消息计数
     * @param conversationId 对话ID
     */
    clearUnreadCount: (conversationId: string) => {
      set(state => ({
        conversations: state.conversations.map(conversation =>
          conversation.id === conversationId
            ? { ...conversation, unreadCount: 0 }
            : conversation
        )
      }));
    },

    /**
     * 获取指定对话的未读消息计数
     * @param conversationId 对话ID
     * @returns 未读消息数量
     */
    getUnreadCount: (conversationId: string): number => {
      const state: ChatState = useChatStore.getState();
      const conversation: ConversationInfo | undefined = state.conversations.find((conv: ConversationInfo) => conv.id === conversationId);
      return conversation?.unreadCount || 0;
    },

    /**
     * 将指定对话移动到列表顶部
     * @param conversationId 对话ID
     */
    moveConversationToTop: (conversationId: string) => {
      set(state => {
        const conversationIndex = state.conversations.findIndex(conv => conv.id === conversationId);
        
        // 如果找不到对话或对话已经在顶部，则不需要移动
        if (conversationIndex <= 0) {
          return state;
        }

        // 创建新的对话列表，将目标对话移到顶部
        const conversations = [...state.conversations];
        const [targetConversation] = conversations.splice(conversationIndex, 1);
        
        return {
          conversations: [targetConversation, ...conversations]
        };
      });
    },

    /**
     * 智能获取用户信息（优先从缓存，缓存没有则从网络获取）
     * @param userId 用户ID
     * @returns 用户信息或null（获取失败时）
     */
    getUserByCacheOrNet: async (userId: string): Promise<UserInfo | null> => {
      const state: ChatState = useChatStore.getState();
      
      // 先检查缓存
      const cachedUser = state.userInfoCache.get(userId);
      if (cachedUser) {
        logPrint('从缓存获取用户信息:', userId);
        return cachedUser;
      }

      // 缓存中没有，从网络获取
      try {
        logPrint('从网络获取用户信息:', userId);
        const userInfo = await UserService.getUserInfo(userId);
        
        // 存储到缓存
        useChatStore.getState().setUserToCache(userId, userInfo);
        
        return userInfo;
      } catch (error) {
        console.error('获取用户信息失败:', userId, error);
        return null;
      }
    },

    /**
     * 将用户信息存储到缓存
     * @param userId 用户ID
     * @param userInfo 用户信息
     */
    setUserToCache: (userId: string, userInfo: UserInfo) => {
      set(state => {
        const newCache = new Map(state.userInfoCache);
        newCache.set(userId, userInfo);
        return {
          userInfoCache: newCache
        };
      });
    },

    /**
     * 清空用户信息缓存
     */
    clearUserCache: () => {
      set({
        userInfoCache: new Map<string, UserInfo>()
      });
    },

    // ================== 翻译设置管理方法 ===========================

    /**
     * 获取指定会话的翻译设置
     * @param conversationId 会话ID
     * @returns 翻译设置（如果没有自定义设置则返回默认设置）
     */
    getTranslationSettings: (conversationId: string): TranslationSettings => {
      const state: ChatState = useChatStore.getState();
      return state.translationSettings[conversationId] || state.defaultTranslationSettings;
    },

    /**
     * 设置指定会话的翻译设置
     * @param conversationId 会话ID
     * @param settings 翻译设置
     */
    setTranslationSettings: (conversationId: string, settings: TranslationSettings) => {
      set(state => ({
        translationSettings: {
          ...state.translationSettings,
          [conversationId]: settings
        }
      }));
    },

    /**
     * 更新指定会话的翻译设置（部分更新）
     * @param conversationId 会话ID
     * @param updates 要更新的设置字段
     */
    updateTranslationSettings: (conversationId: string, updates: Partial<TranslationSettings>) => {
      set(state => {
        const currentSettings = state.translationSettings[conversationId] || state.defaultTranslationSettings;
        return {
          translationSettings: {
            ...state.translationSettings,
            [conversationId]: {
              ...currentSettings,
              ...updates
            }
          }
        };
      });
    },

    /**
     * 移除指定会话的翻译设置（恢复为默认设置）
     * @param conversationId 会话ID
     */
    removeTranslationSettings: (conversationId: string) => {
      set(state => {
        const { [conversationId]: _, ...restSettings } = state.translationSettings;
        return {
          translationSettings: restSettings
        };
      });
    },

    /**
     * 设置全局默认翻译设置
     * @param settings 默认翻译设置
     */
    setDefaultTranslationSettings: (settings: TranslationSettings) => {
      set({
        defaultTranslationSettings: settings
      });
    },

    /**
     * 重置指定会话的翻译设置为默认值
     * @param conversationId 会话ID
     */
    resetTranslationSettings: (conversationId: string) => {
      set(state => {
        const { [conversationId]: _, ...restSettings } = state.translationSettings;
        return {
          translationSettings: restSettings
        };
      });
    }
  }))
);
